import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { apiClient } from '@/services/api';
import type {
  BookingMutationPayload,
  SyncedBookingInfo,
  SyncedBookingStatus,
  SyncedTableInfo,
} from '@/types/api';
import { BOOKING_STATUSES } from '@/constants/bookingStatus';

// Same display rule as the timeline (BookingsTimelineView): use the table name,
// falling back to "Table {posTableId}" when it is missing or blank.
const tableLabel = (t: SyncedTableInfo): string =>
  t.tableName && t.tableName.trim() ? t.tableName : `Table ${t.posTableId}`;

// Same ordering as the timeline: by POS table id, ascending.
const byPosId = (a: SyncedTableInfo, b: SyncedTableInfo) => (a.posTableId ?? 0) - (b.posTableId ?? 0);

/** Optional starting values for a new booking (e.g. created from the timeline). */
export interface BookingPrefill {
  startTime?: string; // ISO-8601 instant
  endTime?: string; // ISO-8601 instant
  tableIds?: number[]; // posTableId values (matches booking.shopTableIds + timeline convention)
  section?: string; // booking section name; when set on create, the field is locked
}

interface BookingFormModalProps {
  restaurantId: number;
  booking: SyncedBookingInfo | null;
  /** Applied only when creating (booking === null). */
  initial?: BookingPrefill;
  /** Restrict the selectable tables to this floor (chosen on the bookings tab). */
  floorId?: number | null;
  onClose: () => void;
  onSuccess?: (saved: SyncedBookingInfo) => void;
}

const pad = (n: number) => String(n).padStart(2, '0');
const dateToLocalInput = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

// Convert an ISO instant (e.g. "2026-05-13T18:30:00Z") to the value expected by
// <input type="datetime-local"> ("YYYY-MM-DDTHH:mm") in the browser's local zone.
const isoToLocalInput = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return dateToLocalInput(d);
};

// Convert a datetime-local value back to a UTC ISO instant.
const localInputToIso = (value: string): string | null => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

// Default end time to start + `hours`, used to seed endTime when the user
// picks a start time but hasn't touched end time yet.
const addHoursToLocalInput = (value: string, hours: number): string => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  d.setHours(d.getHours() + hours);
  return dateToLocalInput(d);
};

const csvToIdList = (csv: string | null | undefined): number[] => {
  if (!csv) return [];
  return csv
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n));
};

export default function BookingFormModal({ restaurantId, booking, initial, floorId, onClose, onSuccess }: BookingFormModalProps) {
  const queryClient = useQueryClient();

  interface FormState {
    startTime: string;
    endTime: string;
    partySize: number | null;
    customerName: string;
    customerPhone: string;
    customerEmail: string;
    tableIds: number[];
    bookingSectionId: number | null;
    status: SyncedBookingStatus;
    notes: string;
    specialRequests: string;
    depositAmount: number;
    depositPaid: boolean;
  }

  const [formData, setFormData] = useState<FormState>({
    startTime: isoToLocalInput(booking?.startTime ?? initial?.startTime),
    endTime: isoToLocalInput(booking?.endTime ?? initial?.endTime),
    partySize: booking?.partySize ?? 2,
    customerName: booking?.customerName ?? '',
    customerPhone: booking?.customerPhone ?? '',
    customerEmail: booking?.customerEmail ?? '',
    tableIds: booking ? csvToIdList(booking.shopTableIds) : initial?.tableIds ?? [],
    // initial?.section (from the timeline's click-to-create) is a section *name*; it's resolved
    // to a posSectionId once the sections query below has loaded (see the effect further down).
    // When editing an existing booking, bookingSectionId is already POS-native on the response.
    bookingSectionId: booking?.bookingSectionId ?? null,
    status: (booking?.status as SyncedBookingStatus | null) ?? 'PENDING',
    notes: booking?.notes ?? '',
    specialRequests: booking?.specialRequests ?? '',
    depositAmount: booking?.depositAmount ?? 0,
    depositPaid: booking?.depositPaid ?? false,
  });

  // Snapshot of the form's initial values, to detect unsaved changes on close.
  const initialFormData = useRef(formData);
  const isDirty = JSON.stringify(formData) !== JSON.stringify(initialFormData.current);

  const handleClose = () => {
    if (isDirty && !window.confirm('Discard unsaved changes?')) {
      return;
    }
    onClose();
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isDirty]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: tables = [] } = useQuery({
    queryKey: ['synced-tables', restaurantId],
    queryFn: () => apiClient.getSyncedTables(restaurantId),
  });

  const { data: sections = [] } = useQuery({
    queryKey: ['booking-sections', restaurantId],
    queryFn: () => apiClient.getBookingSections(restaurantId),
  });

  // Resolve the timeline's name-based prefill (initial.section) to a posSectionId once the
  // sections query has loaded. Only runs for a fresh create with a prefill, and only until a
  // section id is set, so it never overwrites a user's own selection or an edit's existing value.
  useEffect(() => {
    if (booking || !initial?.section || formData.bookingSectionId != null || sections.length === 0) {
      return;
    }
    const match = sections.find((s) => s.name === initial.section);
    if (match?.posSectionId != null) {
      setFormData((f) => ({ ...f, bookingSectionId: match.posSectionId! }));
    }
  }, [sections, initial?.section, booking, formData.bookingSectionId]);

  const isEdit = booking !== null;

  // Keyed by posTableId — booking.shopTableIds and the form's tableIds are both
  // posTableId values, matching the timeline.
  const tableNameLookup = useMemo(() => {
    const map = new Map<number, string>();
    for (const t of tables) {
      if (t.posTableId != null) map.set(t.posTableId, tableLabel(t));
    }
    return map;
  }, [tables]);

  // Floor that scopes the selectable tables. When editing, prefer the floor of
  // the booking's own tables so they remain visible; otherwise use the floor
  // chosen on the bookings tab. Null means show every floor.
  const selectedTablesFloorId = useMemo(() => {
    for (const t of tables) {
      if (t.floorId != null && formData.tableIds.includes(t.posTableId)) return t.floorId;
    }
    return null;
  }, [tables, formData.tableIds]);

  const effectiveFloorId = selectedTablesFloorId ?? floorId ?? null;

  // Tables for the selectable list: only the effective floor, ordered like the
  // timeline. Selections on other floors stay in formData.tableIds (preserved
  // in the payload) even though they are hidden here.
  const sortedTables = useMemo(
    () =>
      tables
        .filter((t) => effectiveFloorId == null || t.floorId === effectiveFloorId)
        .sort(byPosId),
    [tables, effectiveFloorId],
  );

  const buildPayload = (): BookingMutationPayload | null => {
    const startIso = localInputToIso(formData.startTime);
    if (!startIso) return null;
    const endIso = localInputToIso(formData.endTime);
    const idsCsv = formData.tableIds.length ? formData.tableIds.join(',') : null;
    const namesCsv = formData.tableIds.length
      ? formData.tableIds.map((id) => tableNameLookup.get(id) ?? `Table ${id}`).join(',')
      : null;
    const selectedSection = sections.find((s) => s.posSectionId === formData.bookingSectionId) ?? null;
    return {
      startTime: startIso,
      endTime: endIso,
      partySize: formData.partySize || null,
      customerName: formData.customerName.trim() || null,
      customerPhone: formData.customerPhone.trim() || null,
      customerEmail: formData.customerEmail.trim() || null,
      shopTableIds: idsCsv,
      shopTableNames: namesCsv,
      bookingSectionId: formData.bookingSectionId,
      bookingSection: selectedSection?.name ?? null,
      status: formData.status,
      notes: formData.notes.trim() || null,
      specialRequests: formData.specialRequests.trim() || null,
      depositAmount: formData.depositAmount || null,
      depositPaid: formData.depositPaid,
    };
  };

  const createMutation = useMutation({
    mutationFn: (payload: BookingMutationPayload) => apiClient.createSyncedBooking(restaurantId, payload),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['synced-bookings', restaurantId] });
      onSuccess?.(saved);
      onClose();
    },
    onError: (error: unknown) => {
      const e = error as { response?: { data?: { message?: string } } };
      setErrors({ submit: e.response?.data?.message || 'Failed to create booking' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: BookingMutationPayload) =>
      apiClient.updateSyncedBooking(restaurantId, booking!.uuid!, payload),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['synced-bookings', restaurantId] });
      onSuccess?.(saved);
      onClose();
    },
    onError: (error: unknown) => {
      const e = error as { response?: { data?: { message?: string } } };
      setErrors({ submit: e.response?.data?.message || 'Failed to update booking' });
    },
  });

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!formData.startTime) {
      next.startTime = 'Start time is required';
    }
    if (formData.endTime && formData.startTime && formData.endTime <= formData.startTime) {
      next.endTime = 'End time must be after start time';
    }
    if (formData.partySize != null && formData.partySize < 1) {
      next.partySize = 'Party size must be at least 1';
    }
    if (
      formData.customerEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customerEmail)
    ) {
      next.customerEmail = 'Invalid email format';
    }
    if (formData.depositAmount != null && formData.depositAmount < 0) {
      next.depositAmount = 'Deposit must be zero or positive';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const payload = buildPayload();
    if (!payload) {
      setErrors({ submit: 'Could not build request payload' });
      return;
    }
    // Use mutate (not mutateAsync) so a rejected request is fully handled by
    // the mutation's onError (which surfaces the server message inline) and
    // does not escape as an unhandled promise rejection.
    if (isEdit) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const toggleTable = (id: number) => {
    setFormData((prev) => ({
      ...prev,
      tableIds: prev.tableIds.includes(id)
        ? prev.tableIds.filter((x) => x !== id)
        : [...prev.tableIds, id],
    }));
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleClose}
    >
      <div
        className="card max-w-3xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            {isEdit ? 'Edit Booking' : 'New Booking'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {errors.submit && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Time & party */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start *</label>
              <input
                type="datetime-local"
                value={formData.startTime}
                onChange={(e) => {
                  const startTime = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    startTime,
                    endTime: prev.endTime || addHoursToLocalInput(startTime, 2),
                  }));
                }}
                className={`input w-full ${errors.startTime ? 'border-red-500' : ''}`}
              />
              {errors.startTime && <p className="text-red-500 text-xs mt-1">{errors.startTime}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End</label>
              <input
                type="datetime-local"
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                className={`input w-full ${errors.endTime ? 'border-red-500' : ''}`}
              />
              {errors.endTime && <p className="text-red-500 text-xs mt-1">{errors.endTime}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Party size</label>
              <input
                type="number"
                min={1}
                value={formData.partySize ?? ''}
                onChange={(e) =>
                  setFormData({ ...formData, partySize: e.target.value ? Number(e.target.value) : null })
                }
                className={`input w-full ${errors.partySize ? 'border-red-500' : ''}`}
              />
              {errors.partySize && <p className="text-red-500 text-xs mt-1">{errors.partySize}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value as SyncedBookingStatus })
                }
                className="input w-full"
              >
                {BOOKING_STATUSES.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Customer */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer name</label>
              <input
                type="text"
                value={formData.customerName}
                onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={formData.customerPhone}
                onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.customerEmail}
                onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                className={`input w-full ${errors.customerEmail ? 'border-red-500' : ''}`}
              />
              {errors.customerEmail && (
                <p className="text-red-500 text-xs mt-1">{errors.customerEmail}</p>
              )}
            </div>
          </div>

          {/* Section + tables */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
              <select
                value={formData.bookingSectionId != null ? String(formData.bookingSectionId) : ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    bookingSectionId: e.target.value ? parseInt(e.target.value, 10) : null,
                  })
                }
                disabled={!booking && !!initial?.section}
                className="input w-full disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">— None —</option>
                {sections.map((s) => (
                  <option key={s.id} value={s.posSectionId ?? ''} disabled={s.posSectionId == null}>
                    {s.name}{s.posSectionId == null ? ' (unsynced)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tables</label>
              <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2 space-y-1">
                {sortedTables.length === 0 && (
                  <p className="text-xs text-gray-500">No tables available</p>
                )}
                {sortedTables.map((t) => (
                  <label key={t.posTableId} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={formData.tableIds.includes(t.posTableId)}
                      onChange={() => toggleTable(t.posTableId)}
                    />
                    <span>{tableLabel(t)}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Special requests</label>
              <textarea
                rows={2}
                value={formData.specialRequests}
                onChange={(e) => setFormData({ ...formData, specialRequests: e.target.value })}
                className="input w-full"
              />
            </div>
          </div>

          {/* Deposit */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Deposit amount</label>
              <input
                type="number"
                min={0}
                step="0.01"
                value={formData.depositAmount ?? ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    depositAmount: e.target.value ? Number(e.target.value) : 0,
                  })
                }
                className={`input w-full ${errors.depositAmount ? 'border-red-500' : ''}`}
              />
              {errors.depositAmount && (
                <p className="text-red-500 text-xs mt-1">{errors.depositAmount}</p>
              )}
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formData.depositPaid}
                  onChange={(e) => setFormData({ ...formData, depositPaid: e.target.checked })}
                />
                Deposit paid
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSaving}>
              {isSaving ? 'Saving…' : isEdit ? 'Update booking' : 'Create booking'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
